import { useCallback, useEffect, useState } from 'react';
//import "../styles/rfi.css";
import { fetchVendorProfile, selectVendorUser, setRfiId } from 'auth/VendorAuth/vendorUserSlice';
import ErrorPage from 'components/ErrorPage';
import NotFound from 'components/NotFound';
import VendorReadOnlyAttachments from 'components/VendorRfi/VendorReadOnlyAttachments';
import VendorSessionTimeout from 'components/VendorRfi/VendorSessionTimeout';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useParams } from 'react-router-dom';
import { selectAppConfig } from 'store/appConfigSlice';
import { fetchRfiLink, saveElementValuesAsync, selectRfi } from 'store/rfiSlice';
import { fetchVendorResponse, selectVendorResponse } from 'store/vendorResponseSlice';
import Loading from '../components/Loading';
import VendorNav, { vendorNavPages } from '../components/VendorRfi/VendorNav';
import VendorRfiAppBar from '../components/VendorRfi/VendorRfiAppBar';
import VendorRfiAttachments from '../components/VendorRfi/VendorRfiAttachments';
import VendorRfiDetails from '../components/VendorRfi/VendorRfiDetails';
import VendorRfiQandA from '../components/VendorRfi/VendorRfiQandA';
import VendorRfiQuestionnaire from '../components/VendorRfi/VendorRfiQuestionnaire';
import VendorRfiValidation from '../components/VendorRfi/VendorRfiValidation';
import { RfiStatus, sectionNames } from '../types/RfiTypes';

const stickyNav = () => {
  const navHeight = document.getElementById("main-navbar")?.clientHeight;
  document.body.style.paddingTop = `${(navHeight ?? 159) + 10}px`;
};

const VendorRfi = () => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  dayjs.extend(advancedFormat);

  // Constants
  const autoSaveTime = 60000;

  // Hooks
  const dispatch = useAppDispatch();
  const appConfigState = useAppSelector(selectAppConfig);
  const { id } = useParams();
  const user = useAppSelector(selectVendorUser);
  const rfi = useAppSelector(selectRfi);
  const vendorResponse = useAppSelector(selectVendorResponse);

  // State
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [selectedPage, setSelectedPage] = useState(0);
  const [seed, setSeed] = useState(1);
  const reset = () => {
    setSeed(Math.random());
  };
  const rfiArchived =
    rfi.rfi?.status === RfiStatus.POSTED &&
    rfi.rfi?.archiveDate &&
    dayjs.utc() > dayjs.utc(rfi.rfi?.archiveDate).add(1, "day")
      ? true
      : false;

  // Handlers
  const saveValues = useCallback(
    (message: string) => {
      if (
        !isReadOnly &&
        rfi.rfi?.status === RfiStatus.POSTED &&
        (rfi.rfi?.vendorResponseId ?? 0 > 0)
      ) {
        dispatch(saveElementValuesAsync());
      }
    },
    [dispatch, isReadOnly, rfi.rfi?.status, rfi.rfi?.vendorResponseId]
  );

  const onSelectedPageChanged = (page: number) => {
    if (page !== selectedPage) {
      //Save values on page change
      saveValues("Saved");

      setSelectedPage(page);
    }
  };

  const onResponseDeleted = () => {
    setIsReadOnly(true);
    setSelectedPage(vendorNavPages.UNAUTH_DETAILS);
  };

  // Effects
  useEffect(() => {
    const interval = setInterval(() => {
      saveValues("Saved");
    }, autoSaveTime);
    document.title =
      "RFI Assistant" + (rfi.rfi?.title ? ` - ${rfi.rfi?.title}` : "");
    return () => clearInterval(interval);
  }, [rfi.rfi, saveValues]);

  useEffect(() => {
    dispatch(fetchVendorProfile()).finally(() => {
      dispatch(fetchRfiLink(id ?? ""));
    });
  }, []);

  useEffect(() => {
    if (rfi.rfi?.rfiId && rfi.rfi?.vendorResponseId) {
      dispatch(
        fetchVendorResponse({
          rfiId: rfi.rfi?.rfiId,
          vendorResponseId: rfi.rfi?.vendorResponseId,
        })
      );
    }
    if (rfi.rfi?.rfiId) {
      dispatch(setRfiId(rfi.rfi?.rfiId.toString()));
    }
  }, [rfi.rfi?.vendorResponseId]);

  useEffect(() => {
    if (
      user.isAuthenticated &&
      (vendorResponse.vendorResponse?.vendorResponseId ?? 0) > 0
    )
      setSelectedPage(1);
    if (
      user.isAuthenticated &&
      (vendorResponse.vendorResponse?.vendorResponseId ?? 0) > 0 &&
      selectedPage === vendorNavPages.AUTH_DETAILS
    )
      setSelectedPage(1);
  }, [user, vendorResponse.vendorResponse?.vendorResponseId]);

  useEffect(() => {
    if (rfi.rfi?.status && rfi.rfi?.status !== RfiStatus.POSTED) {
      setIsReadOnly(true);
    }
    if (vendorResponse.vendorResponse?.dateSubmitted) {
      setIsReadOnly(true);
    }
    if (rfiArchived) {
      setIsReadOnly(true);
    }

    if (
      rfi.rfi?.responsesDueDate &&
      dayjs(rfi.rfi.responsesDueDate) < dayjs()
    ) {
      setIsReadOnly(true);
    }
  }, [rfi.rfi?.status, vendorResponse.vendorResponse?.dateSubmitted]);

  // Render
  stickyNav();

  if (rfi.error) {
    if (rfi.error === "Request failed with status code 404") {
      return <NotFound></NotFound>;
    }
    return <ErrorPage></ErrorPage>;
  }

  if (rfi.isLoading || vendorResponse.isLoading || user.isLoading)
    return <Loading />;

  // show not found page if the rfiId does not exist
  if (!rfi.rfi?.rfiId) {
    return <NotFound />;
  }

  return (
    <>
      <div className="fixed-top" id="main-navbar">
        <VendorRfiAppBar
          user={user}
          readOnly={isReadOnly}
          onResponseDeleted={onResponseDeleted}
        />
      </div>
      {user.isAuthenticated && <VendorSessionTimeout></VendorSessionTimeout>}
      <div className="container-fluid">
        <div className="row">
          {/* <!-- Sidebar Menu included using jQuery since this is a reusable element --> */}
          <nav
            id="sidebarMenu"
            className="col-md-4 col-lg-3 d-md-block sidebar pl-md-4 pr-md-0"
          >
            {/* <div className="nav flex-column nav-pills sticky-top" id="v-pills-tab" role="tablist" aria-orientation="vertical"> */}
            <VendorNav
              //rfi={rfiState}
              user={user}
              setSelectedPage={onSelectedPageChanged}
              selectedPage={selectedPage}
              readOnly={isReadOnly}
            ></VendorNav>
            {/* </div> */}
          </nav>
          <main role="main" className="col-md-8 ml-sm-auto col-lg-9 px-md-4">
            <div
              className="tab-content tile fillHeight"
              id="v-pills-tabContent"
            >
              {selectedPage === vendorNavPages.UNAUTH_DETAILS && (
                <VendorRfiDetails
                  user={user}
                  setSelectedPage={onSelectedPageChanged}
                ></VendorRfiDetails>
              )}
              {selectedPage === vendorNavPages.UNAUTH_QUESTIONNAIRE && (
                <VendorRfiQuestionnaire
                  // rfi={rfiState}
                  user={user}
                  setSelectedPage={onSelectedPageChanged}
                ></VendorRfiQuestionnaire>
              )}
              {selectedPage === vendorNavPages.AUTH_DETAILS && (
                <VendorRfiDetails
                  //rfi={rfiState}
                  user={user}
                  setSelectedPage={onSelectedPageChanged}
                ></VendorRfiDetails>
              )}
              {selectedPage === vendorNavPages.VENDOR_INFO && (
                <VendorRfiQuestionnaire
                  //rfi={rfiState}
                  user={user}
                  section={sectionNames.VENDOR_INFO}
                  setSelectedPage={onSelectedPageChanged}
                  readOnly={isReadOnly}
                ></VendorRfiQuestionnaire>
              )}
              {selectedPage === vendorNavPages.QUESTIONNAIRE && (
                <VendorRfiQuestionnaire
                  //rfi={rfiState}
                  user={user}
                  section={sectionNames.QUESTIONNAIRE}
                  setSelectedPage={onSelectedPageChanged}
                  readOnly={isReadOnly}
                ></VendorRfiQuestionnaire>
              )}
              {selectedPage === vendorNavPages.ATTACHMENTS && (
                <VendorRfiAttachments
                  key="attachments1"
                  // rfi={rfiState}
                  user={user}
                  setSelectedPage={onSelectedPageChanged}
                  readOnly={isReadOnly}
                ></VendorRfiAttachments>
              )}
              {selectedPage === vendorNavPages.VALIDATE && (
                <>
                  <VendorRfiValidation
                    //rfi={rfiState}
                    user={user}
                    section={sectionNames.VENDOR_INFO}
                    setSelectedPage={onSelectedPageChanged}
                    key={seed}
                    reset={reset}
                    readOnly={isReadOnly}
                  ></VendorRfiValidation>
                </>
              )}
              {selectedPage === vendorNavPages.UNAUTH_ATTACHMENTS && (
                <VendorReadOnlyAttachments
                  // rfi={rfiState}
                  user={user}
                  setSelectedPage={onSelectedPageChanged}
                ></VendorReadOnlyAttachments>
              )}
              {selectedPage === vendorNavPages.QA && (
                <VendorRfiQandA
                  setSelectedPage={onSelectedPageChanged}
                  // rfi={rfiState}
                  user={user}
                  readOnly={isReadOnly}
                ></VendorRfiQandA>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default VendorRfi;
